import React from 'react';
import PropTypes from 'prop-types';
import Skeleton from 'react-loading-skeleton';

import { Box, makeStyles } from '@material-ui/core';

import { getShortNumber } from '~/utils/getShortNumber';
import { Plan } from '~/views/private/overview/Components/icons/Plan';
import { Photo } from '~/views/private/overview/Components/icons/Photo';
import { Play } from '~/views/private/overview/Components/icons/Play';
import { Balcony } from '~/views/private/overview/Components/icons/Balcony';
import { Scan360 } from '~/views/private/overview/Components/icons/Scan360';
import { Gallery } from '~/views/private/overview/Components/icons/Gallery';

const CONTENT_ICONS = {
  plans: Plan,
  images: Photo,
  videos: Play,
  balconies: Balcony,
  scans360: Scan360,
};

const toCount = value => {
  const numeric = Number(value);

  return Number.isFinite(numeric) ? numeric : 0;
};

export const DEFAULT_UPLOADED_CONTENT = [
  { key: 'plans', label: 'Plans', count: 0 },
  { key: 'images', label: 'Photos', count: 0 },
  { key: 'videos', label: 'Videos', count: 0 },
  { key: 'balconies', label: 'Exterior views', count: 0 },
  { key: 'scans360', label: '360 scans', count: 0 },
];

export function mapUploadedContentItems(payload) {
  const floorplans = payload?.floorplans || {};
  const units = payload?.units || {};

  return [
    {
      key: 'plans',
      label: 'Plans',
      count: toCount(floorplans.plans),
    },
    {
      key: 'images',
      label: 'Photos',
      count: toCount(floorplans.images) + toCount(units.images),
    },
    {
      key: 'videos',
      label: 'Videos',
      count: toCount(floorplans.videos) + toCount(units.videos),
    },
    {
      key: 'balconies',
      label: 'Exterior views',
      count: toCount(units.balconies),
    },
    {
      key: 'scans360',
      label: '360 scans',
      count: toCount(floorplans.scans360) + toCount(units.scans360),
    },
  ];
}

const useStyles = makeStyles(() => ({
  container: {
    display: 'flex',
    padding: 'var(--utilities-size-1-000)',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 'var(--utilities-size-1-500)',
    flex: '1 0 0',
    alignSelf: 'stretch',
    borderRadius: 'var(--utilities-border-radius-default)',
    backgroundColor: 'var(--surface-default)',
    boxShadow: 'var(--shadows-subtle)',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: '24px',
    alignSelf: 'stretch',
  },
  title: {
    color: 'var(--content-primary)',
    fontFamily: 'var(--typography-font-family-default) !important',
    fontSize: 'var(--typography-font-size-md)',
    fontStyle: 'normal',
    fontWeight: 700,
    lineHeight: 'var(--typography-line-height-md-line)',
    letterSpacing: 'var(--typography-letter-spacing-default)',
    flex: '1 0 0',
  },
  chips: {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--utilities-size-0-500)',
    alignSelf: 'stretch',
  },
  chip: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 'var(--utilities-size-0-250)',
    flex: '1 1 0',
    minWidth: 0,
    padding: 'var(--utilities-size-0-500) var(--utilities-size-0-750)',
    borderRadius: 'var(--utilities-border-radius-default)',
  },
  chipSuccess: {
    backgroundColor: 'var(--palette-green-100)',
    color: 'var(--content-success)',
  },
  chipEmpty: {
    backgroundColor: 'var(--palette-red-100)',
    color: 'var(--content-danger)',
  },
  chipLoading: {
    backgroundColor: 'var(--surface-pale)',
    color: 'var(--content-secondary)',
  },
  chipLabel: {
    fontFamily: 'var(--typography-font-family-default) !important',
    fontSize: 'var(--typography-font-size-sm)',
    fontStyle: 'normal',
    fontWeight: 500,
    lineHeight: 'var(--typography-line-height-sm-line)',
    letterSpacing: 'var(--typography-letter-spacing-default)',
  },
  chipValue: {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--utilities-size-0-500)',
  },
  chipIcon: {
    width: 'var(--utilities-size-1-250)',
    height: 'var(--utilities-size-1-250)',
    flexShrink: 0,
  },
  chipCount: {
    fontFamily: 'var(--typography-font-family-default) !important',
    fontSize: 'var(--typography-font-size-lg)',
    fontStyle: 'normal',
    fontWeight: 700,
    lineHeight: 'var(--typography-line-height-lg-line)',
    letterSpacing: 'var(--typography-letter-spacing-default)',
  },
  manageButton: {
    display: 'flex',
    maxHeight: 'var(--utilities-size-2-000, 32px)',
    padding:
      'var(--utilities-size-0-375, 6px) var(--utilities-size-0-500, 8px)',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 'var(--utilities-size-0-500, 8px)',
    borderRadius: 'var(--utilities-border-radius-default, 8px)',
    background: 'var(--surface-accent, #5E59F3)',
    boxShadow: '0px 1px 2px 0px rgba(23, 28, 38, 0.05)',
    cursor: 'pointer',
    '&:hover': {
      background: 'var(--surface-hover-accent-hover)',
    },
  },
  manageButtonIcon: {
    width: 'var(--utilities-size-1-250)',
    height: 'var(--utilities-size-1-250)',
  },
  manageButtonText: {
    color: 'var(--content-inverted, #FFF)',
    textAlign: 'center',
    fontFamily: 'var(--typography-font-family-default, Inter) !important',
    fontSize: 'var(--typography-font-size-sm, 14px)',
    fontStyle: 'normal',
    fontWeight: 500,
    lineHeight: 'var(--typography-line-height-sm-line, 20px)',
    letterSpacing: 'var(--typography-letter-spacing-default, 0px)',
  },
}));

export const UploadedContent = ({ items, loading, onManageContent }) => {
  const classes = useStyles();

  return (
    <Box className={classes.container}>
      <Box className={classes.header}>
        <Box className={classes.title}>Uploaded Content</Box>
        <Box className={classes.manageButton} onClick={onManageContent}>
          <Gallery
            className={classes.manageButtonIcon}
            color="var(--content-inverted)"
          />
          <Box className={classes.manageButtonText}>Manage Content</Box>
        </Box>
      </Box>
      <Box className={classes.chips}>
        {items.map(item => {
          const Icon = CONTENT_ICONS[item.key];
          const isEmpty = !item.count;
          const chipClass = loading
            ? classes.chipLoading
            : isEmpty
            ? classes.chipEmpty
            : classes.chipSuccess;

          return (
            <Box key={item.key} className={`${classes.chip} ${chipClass}`}>
              <Box className={classes.chipLabel}>{item.label}</Box>
              <Box className={classes.chipValue}>
                {Icon && (
                  <Icon className={classes.chipIcon} color="currentColor" />
                )}
                <Box className={classes.chipCount}>
                  {loading ? (
                    <Skeleton height="100%" />
                  ) : (
                    getShortNumber(item.count)
                  )}
                </Box>
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

UploadedContent.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      count: PropTypes.number,
    }),
  ),
  loading: PropTypes.bool,
  onManageContent: PropTypes.func,
};

UploadedContent.defaultProps = {
  items: DEFAULT_UPLOADED_CONTENT,
  loading: false,
  onManageContent: undefined,
};
